import { isStaff } from './roles';

const DIRECT_MEDIA =
  /\.(mp3|wav|ogg|m4a|aac|flac|opus|mp4|webm|mov|m4v|avi|mkv|ogv|jpe?g|png|gif|webp|svg)(\?|#|$)/i;
const WATCH_PAGE = /(youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com)/i;

function isExternalUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value.trim());
}

function isAppPath(value) {
  return (
    typeof value === 'string' &&
    value.startsWith('/') &&
    !value.startsWith('//')
  );
}

function isDirectMediaFile(value) {
  return typeof value === 'string' && DIRECT_MEDIA.test(value.trim());
}

function isWatchPageUrl(value) {
  return typeof value === 'string' && WATCH_PAGE.test(value.trim());
}

/** deepLink accidentally set to a media/image file (older notifications). */
function isMediaLikeLink(value, item) {
  if (!value) return false;
  const v = value.trim();
  if (item?.mediaUrl && v === String(item.mediaUrl).trim()) return true;
  if (item?.imageUrl && v === String(item.imageUrl).trim()) return true;
  return isDirectMediaFile(v);
}

function canOpenPublicArticle(item, user) {
  if (!item?.articleId && !item?.deepLink?.startsWith('/article/')) {
    return true;
  }
  if (item.articleAccessible === true) return true;
  if (item.articleAccessible === false) {
    return false;
  }
  return isStaff(user?.role);
}

export function isWorkflowAlert(item) {
  const deep = typeof item?.deepLink === 'string' ? item.deepLink.trim() : '';
  if (deep.startsWith('/admin/')) return true;
  const actions = Array.isArray(item?.actions) ? item.actions : [];
  return actions.some((a) => {
    const url = typeof a?.url === 'string' ? a.url.trim() : '';
    return url.startsWith('/admin/');
  });
}

export function isStoryGone(item) {
  if (!item?.articleId) return false;
  if (item.articleExists === false) return true;
  if (item.articleAccessible === false && !isWorkflowAlert(item)) return true;
  return false;
}

export function storyGoneMessage(item) {
  if (item?.articleExists === false) {
    return 'This story was removed.';
  }
  return 'This story is no longer on the feed.';
}

function playInPageMedia(alertId) {
  if (!alertId || typeof document === 'undefined') return false;
  const el = document.querySelector(
    `[data-alert-media="${CSS.escape(String(alertId))}"]`,
  );
  if (!el) return false;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (typeof el.play === 'function') {
    const result = el.play();
    if (result && typeof result.catch === 'function') {
      result.catch(() => {
        /* autoplay may be blocked — controls are still visible */
      });
    }
  }
  return true;
}

/**
 * Resolve where an alert Open / Listen / Watch / Read should go.
 * Intentional links (article / deepLink / url) win.
 * Media files play in-app; YouTube/Vimeo open as pages.
 * Never dump raw image/media file URLs into a blank new tab as the primary UX.
 */
export function resolveAlertOpenTarget(item) {
  if (!item || typeof item !== 'object') {
    return { type: 'none' };
  }

  const kind = String(item.kind || 'standard');
  const deepLink =
    typeof item.deepLink === 'string' ? item.deepLink.trim() : '';
  const url = typeof item.url === 'string' ? item.url.trim() : '';
  const mediaUrl =
    typeof item.mediaUrl === 'string' ? item.mediaUrl.trim() : '';
  const alertId = item.id ? String(item.id) : '';

  // 1) App routes / article
  if (isAppPath(deepLink) && !isMediaLikeLink(deepLink, item)) {
    return { type: 'navigate', path: deepLink };
  }
  if (item.articleId) {
    return { type: 'navigate', path: `/article/${String(item.articleId)}` };
  }

  // 2) Explicit external link field (not the media file)
  if (isAppPath(url)) {
    return { type: 'navigate', path: url };
  }
  if (isExternalUrl(url) && !isMediaLikeLink(url, item)) {
    return { type: 'external', url };
  }

  // 3) deepLink that is a real web page (e.g. YouTube), not a file
  if (isExternalUrl(deepLink) && !isMediaLikeLink(deepLink, item)) {
    return { type: 'external', url: deepLink };
  }

  // 4) Audio / video media
  if ((kind === 'audio' || kind === 'video') && mediaUrl) {
    if (isWatchPageUrl(mediaUrl)) {
      return { type: 'external', url: mediaUrl };
    }
    if (alertId) {
      return { type: 'play_media', alertId, mediaUrl };
    }
    if (isWatchPageUrl(mediaUrl) || isExternalUrl(mediaUrl)) {
      return { type: 'external', url: mediaUrl };
    }
  }

  // 5) Banner / standard with only image — content is already on the card
  if (kind === 'banner') {
    return { type: 'stay', reason: 'banner' };
  }

  return { type: 'none' };
}

/** True when Open / Listen / Watch / Read would do something useful. */
export function alertHasOpenableTarget(item) {
  const target = resolveAlertOpenTarget(item);
  return (
    target.type === 'navigate' ||
    target.type === 'external' ||
    target.type === 'play_media'
  );
}

/**
 * True when the alert opens real content (article / external URL), not a
 * self-link back to the alerts inbox (typical for plain announcements).
 */
export function alertHasContentDeepLink(item) {
  if (!item || typeof item !== 'object') return false;

  if (item.articleId) return true;

  const actions = Array.isArray(item.actions) ? item.actions : [];
  if (
    actions.some(
      (a) => a?.actionType === 'open_article' || a?.actionType === 'open_url',
    )
  ) {
    return true;
  }

  const deepLink =
    typeof item.deepLink === 'string' ? item.deepLink.trim() : '';
  if (deepLink) {
    if (deepLink.startsWith('/article/')) return true;
    if (isExternalUrl(deepLink) && !isMediaLikeLink(deepLink, item)) {
      return true;
    }
    if (
      isAppPath(deepLink) &&
      !deepLink.startsWith('/alerts') &&
      !isMediaLikeLink(deepLink, item)
    ) {
      return true;
    }
  }

  const url = typeof item.url === 'string' ? item.url.trim() : '';
  if (url) {
    if (isAppPath(url) && !url.startsWith('/alerts')) return true;
    if (isExternalUrl(url) && !isMediaLikeLink(url, item)) return true;
  }

  return false;
}

/**
 * Resolve target from push notification `data` (SW / toast).
 * Article and page links open that page. Media files stay in the alerts player.
 */
export function resolvePushOpenTarget(data = {}) {
  const id = data.id ? String(data.id) : '';
  const kind = String(data.kind || 'standard');
  const deepLink =
    typeof data.deepLink === 'string' ? data.deepLink.trim() : '';
  const url = typeof data.url === 'string' ? data.url.trim() : '';
  const mediaUrl =
    typeof data.mediaUrl === 'string' ? data.mediaUrl.trim() : '';

  if (isAppPath(deepLink) && !isMediaLikeLink(deepLink, data)) {
    if (deepLink.startsWith('/admin')) {
      if (data.id) {
        return {
          type: 'navigate',
          path: `/alerts?nid=${encodeURIComponent(String(data.id))}`,
        };
      }
      return { type: 'navigate', path: '/alerts' };
    }
    return { type: 'navigate', path: deepLink };
  }
  if (isExternalUrl(deepLink) && !isMediaLikeLink(deepLink, data)) {
    try {
      const parsed = new URL(deepLink);
      if (
        typeof window !== 'undefined' &&
        parsed.origin === window.location.origin
      ) {
        return {
          type: 'navigate',
          path: `${parsed.pathname}${parsed.search}${parsed.hash}`,
        };
      }
    } catch {
      /* treat as an external page */
    }
    return { type: 'external', url: deepLink };
  }
  if (data.articleId) {
    return { type: 'navigate', path: `/article/${String(data.articleId)}` };
  }
  if (isAppPath(url) && !isMediaLikeLink(url, data)) {
    return { type: 'navigate', path: url };
  }
  if (isExternalUrl(url) && !isMediaLikeLink(url, data)) {
    return { type: 'external', url };
  }
  if ((kind === 'audio' || kind === 'video') && mediaUrl && isWatchPageUrl(mediaUrl)) {
    return { type: 'external', url: mediaUrl };
  }
  if (id) {
    return { type: 'navigate', path: `/alerts?nid=${encodeURIComponent(id)}` };
  }
  return { type: 'navigate', path: '/alerts' };
}

export function applyOpenTarget(target, navigate, navState) {
  if (!target || target.type === 'none' || target.type === 'stay') {
    return target?.type === 'stay';
  }
  if (target.type === 'navigate' && target.path) {
    navigate(target.path, navState ? { state: navState } : undefined);
    return true;
  }
  if (target.type === 'external' && target.url) {
    window.open(target.url, '_blank', 'noopener,noreferrer');
    return true;
  }
  if (target.type === 'play_media' && target.alertId) {
    const played = playInPageMedia(target.alertId);
    if (played) return true;
    // Media element not mounted yet — go to alerts and play there
    navigate(`/alerts?nid=${encodeURIComponent(target.alertId)}`);
    return true;
  }
  return false;
}

export function openDeepLink(item, navigate, user, showError, navState) {
  const deepLink =
    typeof item?.deepLink === 'string' ? item.deepLink.trim() : '';
  const opts = navState ? { state: navState } : undefined;
  const staff = isStaff(user?.role);
  const articleStillThere = item?.articleExists !== false;

  // Published stories should open on the public article page — even when a
  // stale workflow alert still points at /admin/content/:id/edit.
  if (item?.articleAccessible === true && item?.articleId) {
    navigate(`/article/${String(item.articleId)}`, opts);
    return true;
  }

  const targetsArticle =
    Boolean(item?.articleId) ||
    (item?.deepLink?.startsWith('/article/') &&
      !isMediaLikeLink(item.deepLink, item));

  // Taken down or deleted after the alert was sent — stay here and explain.
  if (isStoryGone(item) || (targetsArticle && !canOpenPublicArticle(item, user))) {
    if (staff && articleStillThere && item?.articleId) {
      navigate(`/admin/content/${item.articleId}/edit`, opts);
      return true;
    }
    showError?.(storyGoneMessage(item));
    return true;
  }

  if (isAppPath(deepLink) && deepLink.startsWith('/admin/')) {
    if (staff) {
      if (item?.articleExists === false) {
        showError?.(storyGoneMessage(item));
        return true;
      }
      navigate(deepLink, opts);
      return true;
    }
    showError?.(storyGoneMessage(item));
    return true;
  }

  const target = resolveAlertOpenTarget(item);
  return applyOpenTarget(target, navigate, navState);
}

export function tryPlayAlertMedia(alertId) {
  return playInPageMedia(alertId);
}
